import { apiClient } from './client';
import type {
  ServicesResponse,
  ServiceResponse,
  ServiceRegionsResponse,
  ServiceFilters,
  CreateServiceData,
  UpdateServiceData,
  ServicePricingData,
  BulkPricingData,
  ServicePricingDetailsResponse,
  RegionPriceUpdateResponse,
  BulkUpdateResponse,
} from '@/types';

/**
 * Services API Client
 * Provides methods for interacting with service endpoints
 */
export const servicesAPI = {
  /**
   * Get all services with optional filtering
   */
  getAll: (params?: ServiceFilters) =>
    apiClient.get<ServicesResponse>('/services', { params }),

  /**
   * Get service by ID with regional pricing
   */
  getById: (id: number) =>
    apiClient.get<ServiceResponse>(`/services/${id}`),

  /**
   * Get all regions and pricing for a service
   */
  getRegions: (serviceId: number, params?: {
    governorate?: string;
    minPrice?: number;
    maxPrice?: number;
    isAvailable?: boolean;
  }) =>
    apiClient.get<ServiceRegionsResponse>(`/services/${serviceId}/regions`, { params }),

  /**
   * Create a new service (Admin only)
   */
  create: (data: CreateServiceData) =>
    apiClient.post<ServiceResponse>('/services', data),

  /**
   * Update an existing service (Admin only)
   */
  update: (id: number, data: UpdateServiceData) =>
    apiClient.put<ServiceResponse>(`/services/${id}`, data),

  /**
   * Delete a service (Admin only)
   */
  delete: (id: number) =>
    apiClient.delete(`/services/${id}`),

  /**
   * Update service pricing for a specific region (Admin only)
   */
  updatePricing: (data: ServicePricingData) =>
    apiClient.post('/service-regions', data),

  /**
   * Bulk update pricing for multiple regions (Admin only)
   */
  bulkUpdatePricing: (data: BulkPricingData) =>
    apiClient.post('/service-regions/bulk', data),

  /**
   * Get detailed pricing for a service (Admin only)
   */
  getPricingDetails: (serviceId: number) =>
    apiClient.get<ServicePricingDetailsResponse>(`/services/${serviceId}/pricing`),

  /**
   * Update price for a single region (Admin only)
   */
  updateServiceRegionPrice: (serviceId: number, regionId: number, price: number) =>
    apiClient.put<RegionPriceUpdateResponse>(
      `/services/${serviceId}/pricing/${regionId}`,
      { price }
    ),

  /**
   * Bulk update all regions to same price (Admin only)
   */
  bulkUpdateAllRegions: (serviceId: number, price: number) =>
    apiClient.put<BulkUpdateResponse>(
      `/services/${serviceId}/pricing/bulk/all`,
      { price }
    ),

  /**
   * Bulk update regions by governorate (Admin only)
   */
  bulkUpdateByGovernorate: (serviceId: number, governorate: string, price: number) =>
    apiClient.put<BulkUpdateResponse>(
      `/services/${serviceId}/pricing/bulk/governorate/${encodeURIComponent(governorate)}`,
      { price }
    ),

  /**
   * Toggle service availability for a region (Admin only)
   */
  toggleServiceAvailability: (serviceId: number, regionId: number) =>
    apiClient.patch(`/services/${serviceId}/regions/${regionId}/toggle`),

  /**
   * Remove service from a region (Admin only)
   */
  removeServiceFromRegion: (serviceId: number, regionId: number) =>
    apiClient.delete(`/services/${serviceId}/regions/${regionId}`),
  /**
   * Route-Based Pricing Methods (for winch services)
   */
  
  /**
   * Get all routes for a service
   */
  getRoutes: (serviceId: number, params?: {
    fromRegionId?: number;
    toRegionId?: number;
    fromGovernorate?: string;
    toGovernorate?: string;
    minPrice?: number;
    maxPrice?: number;
    isAvailable?: boolean;
    page?: number;
    limit?: number;
  }) =>
    apiClient.get(`/services/${serviceId}/routes`, { params }),
  
  /**
   * Get specific route price
   */
  getSpecificRoute: (serviceId: number, fromRegionId: number, toRegionId: number) =>
    apiClient.get(`/services/${serviceId}/routes/${fromRegionId}/${toRegionId}`),
  
  /**
   * Get routes from a specific region
   */
  getRoutesFromRegion: (serviceId: number, regionId: number) =>
    apiClient.get(`/services/${serviceId}/routes/from/${regionId}`),
  
  /**
   * Create/update route pricing (Admin only)
   */
  upsertRoutePricing: (serviceId: number, data: {
    fromRegionId: number;
    toRegionId: number;
    price: number;
    isAvailable?: boolean;
    estimatedTimeMinutes?: number;
    distanceKm?: number;
    notes?: string;
  }) =>
    apiClient.post(`/services/${serviceId}/routes`, data),
  
  /**
   * Bulk create/update routes (Admin only)
   */
  bulkUpdateRoutes: (serviceId: number, routes: Array<{
    fromRegionId: number;
    toRegionId: number;
    price: number;
    isAvailable?: boolean;
    estimatedTimeMinutes?: number;
    distanceKm?: number;
  }>) =>
    apiClient.post(`/services/${serviceId}/routes/bulk`, { routes }),
  
  /**
   * Delete a route (Admin only)
   */
  deleteRoute: (serviceId: number, fromRegionId: number, toRegionId: number) =>
    apiClient.delete(`/services/${serviceId}/routes/${fromRegionId}/${toRegionId}`),
  
  /**
   * Toggle route availability (Admin only)
   */
  toggleRouteAvailability: (serviceId: number, fromRegionId: number, toRegionId: number) =>
    apiClient.patch(`/services/${serviceId}/routes/${fromRegionId}/${toRegionId}/toggle`),
};
